import {
  getHireInterviewsHiringScheduleListQueryOptions,
  getStafferGigsV2CalendarListWithTransformQueryOptions,
  getStafferGigsV2RecurringPatternGigsListWithTransformQueryOptions,
  type ForManagerLocationDetail,
} from "@sidegigllc/api/client";
import { keepPreviousData } from "@tanstack/react-query";
import { addDays, endOfMonth, endOfWeek, startOfMonth, startOfWeek, subDays } from "date-fns";
import { gigStatusOptionIds } from "domains/schedule/containers/Filters/constants";
import { chunkRange, type Range } from "domains/schedule/utils/interviewFeed";
import { getScheduleParams, withWorkingInterviews } from "domains/schedule/utils/scheduleParams";
import type { MarketplaceSearch } from "domains/schedule/utils/useMarketplaceSearch";
import type { WarmableQuery } from "routing/warmQueries";

type QueriesInput = {
  search: MarketplaceSearch;
  location: ForManagerLocationDetail | undefined;
  /** Also request working-interview shifts and the hiring-schedule feed (`jobs-in-main-menu` on, Jobs access). */
  includeWorkingInterviews?: boolean;
  /** Also request task shifts (`qwick-tasks` on). */
  includeTasks?: boolean;
  /** Overrides the calendar's month window: the Schedule list asks for its own, from today. */
  range?: Range;
};

const setup = ({ search, location, includeTasks, range: rangeOverride }: QueriesInput) => {
  const date = search.calendar_date ?? new Date();
  const range = rangeOverride ?? {
    start: subDays(startOfWeek(startOfMonth(date)), 1),
    end: addDays(endOfWeek(endOfMonth(date)), 2),
  };
  const { params, enabled: paramsEnabled } = getScheduleParams(search, "calendar", { includeTasks });
  const query = {
    refetchOnMount: false,
    placeholderData: keepPreviousData,
    enabled: paramsEnabled && location != null,
  };
  return { range, params, query };
};

export const calendarGigsQuery = (input: QueriesInput) => {
  const { range, params, query } = setup(input);
  const types = withWorkingInterviews(params.types, input.includeWorkingInterviews);
  return getStafferGigsV2CalendarListWithTransformQueryOptions(
    {
      start_time: range.start,
      end_time: range.end,
      location_id: input.location?.id,
      ordering: params.ordering,
      skill_ids: params.skill_ids,
      statuses: params.statuses,
      types,
      has_event: params.has_event,
      employment_types: params.employment_types,
    },
    { query },
  );
};

/**
 * Every gig on the calendar's window, whatever the Schedule's filters (status, type, position, events) say and
 * cancelled ones included: what an interview row is deduped against. Only needed with the feed on.
 */
export const loadedGigsQuery = (input: QueriesInput) => {
  const query = calendarGigsQuery({
    ...input,
    search: { calendar_date: input.search.calendar_date, statuses: gigStatusOptionIds },
  });
  return { ...query, enabled: query.enabled && interviewFeedEnabled(input) };
};

/** `loadedGigsQuery` per feed chunk, so a window longer than the calendar's is still asked for in calendar-sized parts. */
export const loadedGigsQueries = (input: QueriesInput & { range: Range }) =>
  chunkRange(input.range).map((range) => loadedGigsQuery({ ...input, range }));

export const recurringGigsQuery = (input: QueriesInput) => {
  const { range, params, query } = setup(input);
  // Tasks do not recur.
  const types = params.types?.filter((type) => type !== "TASK");
  return getStafferGigsV2RecurringPatternGigsListWithTransformQueryOptions(
    {
      start_time: range.start,
      end_time: range.end,
      location_id: input.location?.id ?? -1,
      skill_ids: params.skill_ids,
      types,
    },
    // A Task-only filter leaves nothing to recur; an empty `types` would read as no filter.
    { query: { ...query, enabled: query.enabled && types?.length !== 0 } },
  );
};

/** What the recurring query contributes: nothing when it is switched off (a Task-only filter), never `undefined` that would hold the calendar back. */
export const recurringPhantoms = <T>(query: { enabled?: unknown }, data: Array<T> | undefined): Array<T> | undefined =>
  query.enabled === false ? [] : data;

const interviewFeedEnabled = (input: QueriesInput) => !!input.includeWorkingInterviews;

/** The feed is the calendar's window in chunks of at most 7 weeks; week, day and month all draw from it. */
export const interviewFeedQueries = (input: QueriesInput) => {
  const { range } = setup(input);
  const enabled = interviewFeedEnabled(input);
  return chunkRange(range).map((chunk) =>
    getHireInterviewsHiringScheduleListQueryOptions(
      { start_time: chunk.start, end_time: chunk.end },
      { query: { enabled, refetchOnMount: false, placeholderData: keepPreviousData } },
    ),
  );
};

export const queries = (input: QueriesInput): Array<WarmableQuery> => [
  calendarGigsQuery(input),
  recurringGigsQuery(input),
  ...interviewFeedQueries(input),
];
